import { useEffect, useCallback, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from '@/hooks/useAuth'

interface DropOffEventData {
  eventType: string
  actionContext?: string
  metadata?: Record<string, any>
}

// TTV (Time-to-Value) Storage Keys
const TTV_START_KEY = 'ttv_listing_creation_start'
const TTV_SESSION_KEY = 'ttv_listing_session_id'

// Generate a simple session ID for tracking
const getSessionId = (): string => {
  let sessionId = sessionStorage.getItem('tracking_session_id')
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    sessionStorage.setItem('tracking_session_id', sessionId)
  }
  return sessionId
}

// TTV Helper functions
const startTTVTimer = (): string => {
  const startTime = Date.now().toString()
  const sessionId = `ttv_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  sessionStorage.setItem(TTV_START_KEY, startTime)
  sessionStorage.setItem(TTV_SESSION_KEY, sessionId)
  return sessionId
}

const getTTVDuration = (): { durationMs: number; durationMinutes: number; sessionId: string } | null => {
  const startTime = sessionStorage.getItem(TTV_START_KEY)
  const sessionId = sessionStorage.getItem(TTV_SESSION_KEY)
  if (!startTime || !sessionId) return null
  
  const durationMs = Date.now() - parseInt(startTime, 10)
  const durationMinutes = Math.round(durationMs / 1000 / 60 * 100) / 100 // Round to 2 decimals
  return { durationMs, durationMinutes, sessionId }
}

const clearTTVTimer = () => {
  sessionStorage.removeItem(TTV_START_KEY)
  sessionStorage.removeItem(TTV_SESSION_KEY)
}

export const useDropOffTracking = () => {
  const location = useLocation()
  const { user } = useAuth()
  const lastPageRef = useRef<string>('')
  const [isHost, setIsHost] = useState(false)

  // Check if user is a host
  useEffect(() => {
    const checkHostStatus = async () => {
      if (!user) {
        setIsHost(false)
        return
      }
      const { data } = await supabase
        .from('profiles')
        .select('host_approved')
        .eq('user_id', user.id)
        .single()
      setIsHost(data?.host_approved || false)
    }
    checkHostStatus()
  }, [user])

  const getUserType = useCallback((): 'guest' | 'host' => {
    return isHost ? 'host' : 'guest'
  }, [isHost])

  const trackEvent = useCallback(async (data: DropOffEventData) => {
    try {
      const eventData = {
        user_id: user?.id || null,
        user_type: getUserType(),
        event_type: data.eventType,
        page_path: location.pathname,
        action_context: data.actionContext || null,
        metadata: data.metadata || {},
        session_id: getSessionId()
      }

      // Use anon client to insert - works even without auth
      await supabase.from('user_drop_off_events').insert(eventData)
    } catch (error) {
      console.error('Error tracking drop-off event:', error)
    }
  }, [user, location.pathname, getUserType])

  // Track page exits
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Use sendBeacon for reliable tracking on page exit
      const eventData = {
        user_id: user?.id || null,
        user_type: getUserType(),
        event_type: 'page_exit',
        page_path: location.pathname,
        action_context: null,
        metadata: {},
        session_id: getSessionId()
      }

      // Use sendBeacon for reliable exit tracking
      const blob = new Blob([JSON.stringify(eventData)], { type: 'application/json' })
      navigator.sendBeacon('/api/track-exit', blob)
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [user, location.pathname, getUserType])

  // Track page navigation (when user leaves a page within the app)
  useEffect(() => {
    if (lastPageRef.current && lastPageRef.current !== location.pathname) {
      // User navigated away from the last page - could be tracked if needed
    }
    lastPageRef.current = location.pathname
  }, [location.pathname])

  // Start TTV timer when user begins listing creation
  const startListingCreationTTV = useCallback(() => {
    const sessionId = startTTVTimer()
    trackEvent({
      eventType: 'ttv_listing_start',
      actionContext: 'listing_creation_started',
      metadata: { ttvSessionId: sessionId }
    })
    return sessionId
  }, [trackEvent])

  // Complete TTV tracking when listing is successfully created
  const completeListingCreationTTV = useCallback((listingId: string, listingType: 'single' | 'hotel') => {
    const ttvData = getTTVDuration()
    if (!ttvData) {
      console.warn('TTV timer not found - listing creation was not tracked from start')
      return
    }

    trackEvent({
      eventType: 'ttv_listing_complete',
      actionContext: 'listing_creation_completed',
      metadata: {
        listingId,
        listingType,
        ttvSessionId: ttvData.sessionId,
        durationMs: ttvData.durationMs,
        durationMinutes: ttvData.durationMinutes,
        durationFormatted: ttvData.durationMinutes < 1 
          ? `${Math.round(ttvData.durationMs / 1000)}s` 
          : `${ttvData.durationMinutes}min`
      }
    })

    clearTTVTimer()
  }, [trackEvent])

  // Track TTV abandonment with duration
  const trackListingCreationAbandonWithTTV = useCallback((step: string) => {
    const ttvData = getTTVDuration()
    
    trackEvent({
      eventType: 'listing_creation_abandon',
      actionContext: `listing_creation_${step}`,
      metadata: { 
        step,
        ...(ttvData && {
          ttvSessionId: ttvData.sessionId,
          durationMs: ttvData.durationMs,
          durationMinutes: ttvData.durationMinutes
        })
      }
    })

    clearTTVTimer()
  }, [trackEvent])

  // Host Acquisition Funnel Tracking
  const trackFunnelStep = useCallback((step: string, metadata?: Record<string, any>) => {
    trackEvent({
      eventType: 'host_funnel',
      actionContext: step,
      metadata: {
        funnelStep: step,
        ...metadata
      }
    })
  }, [trackEvent])

  return {
    trackFormAbandon: (formName: string, step?: string, fieldData?: Record<string, any>) => {
      trackEvent({
        eventType: 'form_abandon',
        actionContext: step ? `${formName}_${step}` : formName,
        metadata: { formName, step, ...fieldData }
      })
    },
    trackBookingAbandon: (step: string, listingId?: string) => {
      trackEvent({
        eventType: 'booking_abandon',
        actionContext: `booking_${step}`,
        metadata: { step, listingId }
      })
    },
    trackListingCreationAbandon: trackListingCreationAbandonWithTTV,
    trackButtonClick: (buttonName: string, context?: string) => {
      trackEvent({
        eventType: 'button_interaction',
        actionContext: buttonName,
        metadata: { buttonName, context }
      })
    },
    trackError: (errorType: string, errorMessage: string, context?: string) => {
      trackEvent({
        eventType: 'user_error',
        actionContext: errorType,
        metadata: { errorType, errorMessage, context }
      })
    },
    trackSearch: (searchData: { 
      destination?: string, 
      checkin?: string, 
      checkout?: string, 
      guests?: number,
      resultsCount?: number,
      searchType?: 'main' | 'minimal' | 'advanced'
    }) => {
      trackEvent({
        eventType: 'search_interaction',
        actionContext: searchData.destination || 'no_destination',
        metadata: { 
          ...searchData,
          hasResults: (searchData.resultsCount ?? 0) > 0
        }
      })
    },
    // Host Acquisition Funnel
    trackFunnelStep,
    trackLandingPageView: () => trackFunnelStep('landing_page_view'),
    trackBecomeHostClick: (source?: string) => trackFunnelStep('become_host_click', { source }),
    trackHostSignupStart: () => trackFunnelStep('host_signup_start'),
    trackEmailVerified: () => trackFunnelStep('email_verified'),
    trackListingFormStart: () => trackFunnelStep('listing_form_start'),
    trackFirstPhotoUpload: () => trackFunnelStep('first_photo_upload'),
    trackListingPublished: (listingId: string, listingType: string) => 
      trackFunnelStep('listing_published', { listingId, listingType }),
    // TTV Tracking functions
    startListingCreationTTV,
    completeListingCreationTTV,
    trackCustomEvent: trackEvent
  }
}
